<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Asset, SubAsset } from '@/models/api-generated'
import useSubAssetColumns from './columns'
import CustomHeader from '../custom_field/CustomHeader.vue'
import CustomRow from '../custom_field/CustomRow.vue'
import { getResponsibleIcon } from '@/services/utils'
import { type CustomValueMap, fetchCustomValues } from '@/services/custom'
import sliderStore from '@/stores/slider'

const props = defineProps<{
	component: string
	asset: Asset
	disableRowClick: boolean
}>()

const slider = sliderStore()
const { t } = useI18n()
const { columns, custom } = useSubAssetColumns(props.component)
const model = defineModel<SubAsset[]>({ required: true })
const customValues = ref<CustomValueMap>()

watch(
	model,
	async () => {
		customValues.value = await fetchCustomValues('sub_assets', ...model.value.map((x) => x.id))
	},
	{ immediate: true },
)

function rowClick(e: MouseEvent, sub: SubAsset) {
	const el = e.target as HTMLElement
	if (el.tagName === 'I') return

	slider.openSubAsset(props.asset, sub.id, `${sub.name} (${props.asset.name})`)
}
</script>

<template>
	<table class="table">
		<thead>
			<th v-if="columns.inventoryNr.value.isVisible">
				{{ $t('asset.inventoryNr') }}
			</th>
			<th v-if="columns.name.value.isVisible">
				{{ $t('misc.name') }}
			</th>
			<th v-if="columns.location.value.isVisible">
				{{ $t('location') }}
			</th>
			<th v-if="columns.price.value.isVisible">
				{{ $t('price') }}
			</th>
			<th v-if="columns.responsible.value.isVisible">
				{{ $t('responsible') }}
			</th>
			<th v-if="columns.status.value.isVisible">
				{{ $t('asset.status.label') }}
			</th>
			<th v-if="columns.comment.value.isVisible">
				{{ $t('misc.comment') }}
			</th>
			<CustomHeader :columns="custom" :disable-order="true"></CustomHeader>
		</thead>
		<tbody>
			<tr v-for="sub in model" :key="sub.id" @mouseup.stop="(e) => !props.disableRowClick && rowClick(e, sub)" class="blue">
				<td v-if="columns.inventoryNr.value.isVisible">
					{{ sub.inventoryNr }}
				</td>
				<td v-if="columns.name.value.isVisible">
					{{ sub.name }}
				</td>
				<td v-if="columns.location.value.isVisible">
					{{ sub.location }}
				</td>
				<td v-if="columns.price.value.isVisible">
					{{ sub.price }}
				</td>
				<td v-if="columns.responsible.value.isVisible">
					<i v-if="sub.responsible" :class="getResponsibleIcon(sub.responsible)"></i>
					{{ sub.responsible ? sub.responsible.label : '-' }}
				</td>
				<td v-if="columns.status.value.isVisible">
					<i v-if="sub.active" v-one-tooltip="t('asset.status.active')" class="bi bi-circle-fill active"></i>
					<i v-else v-one-tooltip="t('asset.status.inactive')" class="bi bi-circle-fill inactive"></i>
				</td>
				<td v-if="columns.comment.value.isVisible">
					{{ sub.comment }}
				</td>
				<CustomRow :columns="custom" v-model="customValues" :row-id="sub.id"></CustomRow>
			</tr>
		</tbody>
	</table>
</template>

<style lang="sass" scoped>
:deep(.blue td)
    color: var(--bs-primary)
    background-color: var(--bs-primary-bg-subtle)
    border-bottom-color: rgba(var(--bs-primary-rgb), .6)

tr.submethod > td
        cursor: pointer
        white-space: nowrap

tr.group-row > td
    cursor: default

td
    text-overflow: ellipsis
</style>
